import React from 'react';
import { useCreditsContext } from '../context/CreditsContext';
import {
  LuTarget as Target,
  LuSparkles as Sparkles,
  LuZap as Zap,
  LuBriefcase as Briefcase,
  LuExternalLink as ExternalLink,
} from 'react-icons/lu';

interface SidebarProps {
  keywords: string;
  setKeywords: (v: string) => void;
  tone: string;
  setTone: (v: string) => void;
  tlds: string[];
  setTlds: (v: string[]) => void;
  strategy: string[];
  setStrategy: (v: string[]) => void;
  length: string;
  setLength: (v: string) => void;
  onGenerate: () => void;
  loading: boolean;
}

const TONES = [
  { value: 'professional', label: 'Professional', icon: Target },
  { value: 'playful', label: 'Playful', icon: Sparkles },
  { value: 'technical', label: 'Technical', icon: Zap },
  { value: 'bold', label: 'Bold', icon: Briefcase },
];

const STRATEGIES = ['Compound', 'Invented', 'Real word', 'Initialism'];
const TLD_OPTIONS = ['.com', '.io', '.co', '.ai', '.dev'];
const LENGTHS = [
  { value: 'short', label: 'Short', hint: '4-6' },
  { value: 'medium', label: 'Medium', hint: '6-9' },
  { value: 'long', label: 'Long', hint: '9+' },
];

function toggleItem(arr: string[], item: string): string[] {
  return arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item];
}

export const Sidebar: React.FC<SidebarProps> = ({
  keywords,
  setKeywords,
  tone,
  setTone,
  tlds,
  setTlds,
  strategy,
  setStrategy,
  length,
  setLength,
  onGenerate,
  loading,
}) => {
  const { credits, hasUnlimitedCredits, timeUntilReset, userTier } = useCreditsContext();

  const maxCredits = userTier === 'anonymous' ? 3 : userTier === 'free' ? 10 : 20;
  const displayMax = hasUnlimitedCredits ? 20 : maxCredits;
  const displayCredits = hasUnlimitedCredits ? displayMax : Math.min(credits, displayMax);
  const creditPct = hasUnlimitedCredits ? 100 : Math.round((credits / maxCredits) * 100);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.metaKey) {
      e.preventDefault();
      onGenerate();
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#09090f]">
      <div className="flex-1 overflow-y-auto px-4 py-5 space-y-6">
        {/* Brief textarea */}
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-widest mb-2">
            Your Brief
          </label>
          <textarea
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. AI tool that helps developers write better code faster"
            rows={4}
            disabled={loading}
            className="w-full px-3 py-2.5 bg-[#131320] border border-[#1e1e35] rounded-xl text-sm text-gray-100 placeholder-gray-600 resize-none transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-60"
          />
        </div>

        {/* Regenerate button */}
        <div>
          <button
            onClick={onGenerate}
            disabled={loading || !keywords.trim() || credits === 0}
            className="w-full py-2.5 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 shadow-lg shadow-indigo-900/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Generating…
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                Regenerate
              </>
            )}
          </button>
          <p className="text-center text-[11px] text-gray-600 mt-1.5">
            Uses 1 credit · ⌘↵
          </p>
        </div>

        {/* Tone */}
        <ConfigSection label="Tone">
          <div className="grid grid-cols-2 gap-1.5">
            {TONES.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => setTone(value)}
                disabled={loading}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                  tone === value
                    ? 'bg-[#251c5c] border-[#6855e0] text-[#c8bfff]'
                    : 'bg-[#131320] border-[#1e1e35] text-gray-400 hover:border-[#4a3db0] hover:text-gray-200'
                } disabled:opacity-40`}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                {label}
              </button>
            ))}
          </div>
        </ConfigSection>

        {/* Naming strategy */}
        <ConfigSection label="Naming Strategy">
          <div className="flex flex-wrap gap-1.5">
            {STRATEGIES.map((s) => (
              <button
                key={s}
                onClick={() => setStrategy(toggleItem(strategy, s))}
                disabled={loading}
                className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition-all ${
                  strategy.includes(s)
                    ? 'bg-[#251c5c] border-[#6855e0] text-[#c8bfff]'
                    : 'bg-[#131320] border-[#1e1e35] text-gray-400 hover:border-[#4a3db0] hover:text-gray-200'
                } disabled:opacity-40`}
              >
                {s}
              </button>
            ))}
          </div>
        </ConfigSection>

        {/* TLDs */}
        <ConfigSection label="TLDs">
          <div className="flex flex-wrap gap-1.5">
            {TLD_OPTIONS.map((tld) => (
              <button
                key={tld}
                onClick={() => {
                  const next = toggleItem(tlds, tld);
                  if (next.length > 0) setTlds(next);
                }}
                disabled={loading}
                className={`px-2.5 py-1 rounded-lg border font-mono text-xs font-medium transition-all ${
                  tlds.includes(tld)
                    ? 'bg-[#251c5c] border-[#6855e0] text-[#c8bfff]'
                    : 'bg-[#131320] border-[#1e1e35] text-gray-400 hover:border-[#4a3db0] hover:text-gray-200'
                } disabled:opacity-40`}
              >
                {tld}
              </button>
            ))}
          </div>
        </ConfigSection>

        {/* Name length */}
        <ConfigSection label="Name Length">
          <div className="flex gap-1.5">
            {LENGTHS.map(({ value, label, hint }) => (
              <button
                key={value}
                onClick={() => setLength(length === value ? 'any' : value)}
                disabled={loading}
                className={`flex-1 px-2 py-1.5 rounded-lg border text-xs font-medium transition-all text-center ${
                  length === value
                    ? 'bg-[#251c5c] border-[#6855e0] text-[#c8bfff]'
                    : 'bg-[#131320] border-[#1e1e35] text-gray-400 hover:border-[#4a3db0] hover:text-gray-200'
                } disabled:opacity-40`}
              >
                <div>{label}</div>
                <div className="text-gray-600 font-normal text-[10px]">{hint}</div>
              </button>
            ))}
          </div>
        </ConfigSection>
      </div>

      {/* Credits — pinned bottom */}
      <div className="px-4 py-4 border-t border-[#131325]">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">Credits</span>
          <span className={`text-xs font-bold ${credits === 0 ? 'text-red-400' : 'text-white'}`}>
            {displayCredits} / {displayMax}
          </span>
        </div>

        <div className="h-1 rounded-full bg-[#131320] overflow-hidden mb-1.5">
          <div
            className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-500"
            style={{ width: `${Math.min(creditPct, 100)}%` }}
          />
        </div>

        <div className="flex items-center justify-between">
          {timeUntilReset && (
            <p className="text-[11px] text-gray-600">
              {(() => {
                const resetDate = new Date(Date.now() + (timeUntilReset.hours * 60 + timeUntilReset.minutes) * 60 * 1000);
                return `Resets ${resetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
              })()}
            </p>
          )}
          <a
            href="/pricing"
            className="flex items-center gap-1 text-[11px] font-medium text-indigo-500 hover:text-indigo-400 transition-colors"
          >
            Get more <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </div>
      </div>
    </div>
  );
};

function ConfigSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest mb-2">{label}</p>
      {children}
    </div>
  );
}
